import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { fmtDate } from "../lib/templates";
import { useAuth } from "../lib/AuthContext";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsById, setStatsById] = useState({});
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/invitations");
      setItems(r.data);
      const entries = await Promise.all(
        r.data.map((inv) =>
          api.get(`/invitations/${inv.id}/rsvps/stats`)
            .then((res) => [inv.id, res.data])
            .catch(() => [inv.id, null])
        )
      );
      setStatsById(Object.fromEntries(entries));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this invitation?")) return;
    await api.delete(`/invitations/${id}`);
    toast.success("Deleted");
    load();
  };

  const totals = Object.values(statsById).reduce((acc, s) => {
    if (!s) return acc;
    acc.total_responses += s.total_responses || 0;
    acc.total_guests += s.total_guests || 0;
    acc.yes += s.yes?.count || 0;
    acc.maybe += s.maybe?.count || 0;
    acc.no += s.no?.count || 0;
    return acc;
  }, { total_responses: 0, total_guests: 0, yes: 0, maybe: 0, no: 0 });

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="dashboard-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-3">Your Studio</div>
            <h1 className="font-serif-display text-5xl md:text-6xl font-light tracking-tight">My Weddings</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="dashboard-admin-btn" className="px-5 py-3 bg-stone-100 border border-stone-300 text-stone-800 text-xs uppercase tracking-[0.3em] hover:bg-stone-200 flex items-center gap-2">
                <span>⚙</span> Admin Dashboard
              </Link>
            )}
            <Link to="/templates" data-testid="dashboard-new-btn" className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-[0.3em] hover:bg-stone-800">+ New Invitation</Link>
          </div>
        </div>

        {/* HOW TO FIND RSVP LINK */}
        {items.length > 0 && (
          <div className="mb-8 bg-amber-50 border border-amber-200 p-5 flex flex-wrap items-start gap-4" data-testid="dashboard-help-banner">
            <div className="text-2xl">📨</div>
            <div className="flex-1 text-sm text-stone-700">
              <div className="font-serif-display text-lg mb-1">Looking for the RSVP link?</div>
              <div>Open any invitation below → click <span className="font-semibold">"View RSVPs →"</span> → copy your <span className="font-semibold">Public RSVP Link</span> at top, or add guests + send personalized invites with one click.</div>
            </div>
          </div>
        )}

        {/* OVERALL RSVP SUMMARY */}
        {items.length > 0 && (
          <div className="mb-10 bg-white border border-stone-200 p-6" data-testid="dashboard-rsvp-summary">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Your RSVP overview</div>
                <div className="font-serif-display text-3xl mt-1">Across all invitations</div>
              </div>
              <div className="text-xs text-stone-500">Tap any invitation below for the full RSVP tracker</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <SummaryTile label="Total Responses" value={totals.total_responses} />
              <SummaryTile label="Total Guests" value={totals.total_guests} sub="Across all RSVPs" />
              <SummaryTile label="Attending" value={totals.yes} color="#059669" />
              <SummaryTile label="Maybe" value={totals.maybe} color="#D97706" />
              <SummaryTile label="Declined" value={totals.no} color="#B91C1C" />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-stone-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-stone-300 p-16 text-center">
            <div className="font-serif-display text-3xl mb-3">A blank canvas awaits.</div>
            <div className="text-stone-600 mb-6">Choose a template to begin your first invitation.</div>
            <Link to="/templates" className="inline-block px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-[0.3em]">Browse templates</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((inv) => {
              const s = statsById[inv.id];
              return (
                <div key={inv.id} className="bg-white border border-stone-200 hover:shadow-lg transition-all flex flex-col" data-testid={`inv-card-${inv.id}`}>
                  <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                    <img src={inv.data?.cover_image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-serif-display text-2xl truncate">{inv.title}</div>
                        <div className="text-xs text-stone-500 uppercase tracking-[0.2em] mt-1 truncate">{inv.template_id.replace(/_/g, " ")}</div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 flex-shrink-0 ${inv.paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {inv.paid ? "Live · Paid" : "Draft"}
                      </span>
                    </div>
                    <div className="text-sm text-stone-500 mt-2">Updated {fmtDate(inv.updated_at)}</div>

                    <div className="mt-4 border border-stone-200 bg-stone-50 p-3" data-testid={`inv-rsvp-mini-${inv.id}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-stone-600">RSVPs so far</div>
                        {s && s.total_responses > 0 ? (
                          <div className="font-serif-display text-xl leading-none">{s.total_responses}</div>
                        ) : (
                          <div className="text-xs text-stone-400 italic">No replies yet</div>
                        )}
                      </div>
                      {s && s.total_responses > 0 && (
                        <div className="flex gap-3 text-xs">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "#059669" }} /><span className="font-semibold" style={{ color: "#059669" }}>{s.yes?.count || 0}</span> yes</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "#D97706" }} /><span className="font-semibold" style={{ color: "#D97706" }}>{s.maybe?.count || 0}</span> maybe</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "#B91C1C" }} /><span className="font-semibold" style={{ color: "#B91C1C" }}>{s.no?.count || 0}</span> no</span>
                          <span className="ml-auto text-stone-500"><span className="font-semibold text-stone-800">{s.total_guests}</span> guests</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Link to={`/editor/${inv.id}`} data-testid={`inv-edit-${inv.id}`} className="text-center py-2 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100">Edit</Link>
                      <Link to={`/preview/${inv.id}`} data-testid={`inv-preview-${inv.id}`} className="text-center py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.2em] hover:bg-stone-800">Preview</Link>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {inv.paid ? (
                        <Link to={`/i/${inv.slug}`} data-testid={`inv-public-${inv.id}`} className="text-center py-2 bg-emerald-600 text-white text-xs uppercase tracking-[0.2em] hover:bg-emerald-700">Public Link</Link>
                      ) : (
                        <Link to={`/checkout/${inv.id}`} data-testid={`inv-publish-${inv.id}`} className="text-center py-2 bg-amber-500 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-amber-400 font-semibold">Publish →</Link>
                      )}
                      <Link to={`/rsvps/${inv.id}`} data-testid={`inv-rsvps-${inv.id}`} className="text-center py-2 bg-indigo-600 text-white text-xs uppercase tracking-[0.2em] hover:bg-indigo-700">View RSVPs →</Link>
                    </div>
                    <Link to={`/guestbook/${inv.id}`} data-testid={`inv-guestbook-${inv.id}`} className="mt-2 text-center py-2 bg-rose-50 text-rose-700 border border-rose-200 text-xs uppercase tracking-[0.2em] hover:bg-rose-100 block">Guestbook & Photo-wall</Link>
                    <Link to={`/qr/${inv.id}`} target="_blank" rel="noreferrer" data-testid={`inv-qr-${inv.id}`} className="mt-2 text-center py-2 bg-purple-50 text-purple-700 border border-purple-200 text-xs uppercase tracking-[0.2em] hover:bg-purple-100 block">📇 Guest QR Cards (print)</Link>
                    <button onClick={() => del(inv.id)} data-testid={`inv-delete-${inv.id}`} className="mt-3 text-xs text-stone-500 hover:text-red-700 underline underline-offset-4 self-start">Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, color = "#1A1918" }) {
  return (
    <div className="border border-stone-200 p-4 bg-[#FDFBF7]">
      <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{label}</div>
      <div className="font-serif-display text-3xl mt-1" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}
